/**
 * reportNaming.js - file names for the exported PDF reports.
 *
 *   BIGProject_<ProjectName>_DaylightAnalysis_<DDMMYYYY>_<HHMM>.pdf
 *   BIGProject_<ProjectName>_EnergyModelling_<DDMMYYYY>_<HHMM>.pdf
 *
 * The stamp is LOCAL device time on a 24 hour clock, zero padded, so a report's name matches the
 * clock of whoever produced it. Date order is day-month-year to match the rest of the app's
 * Indian-locale reporting, which is also why it cannot be sorted lexically: the folder holds few
 * enough reports that the readable order is worth more than a sortable one.
 */

/** The two report kinds, each with its filename token and its folder under "PDF reports". */
export const REPORT_KINDS = {
  daylight: { token: 'DaylightAnalysis', folder: 'Daylight report', label: 'Daylight report' },
  energy:   { token: 'EnergyModelling',  folder: 'EM report',       label: 'Energy report' },
  // CFD Analysis (Export & Run tile, src/engine/cfd/cfdReport.js)
  cfd:      { token: 'CFDAnalysis',      folder: 'CFD report',      label: 'CFD report' },
};

const pad = (n) => String(n).padStart(2, '0');

/**
 * Make a project name safe as a filename component on Windows, macOS and Linux.
 *
 * Unicode is PRESERVED: a project named in Hindi or with accented Latin keeps its letters, since
 * every target filesystem stores them fine. Only the genuinely illegal characters go, which is
 * the Windows set (the strictest): < > : " / \ | ? * and the control range. Spaces collapse to
 * single underscores so the name survives a copy-paste into a terminal without quoting.
 */
export function sanitizeProjectNameForFile(name) {
  const cleaned = String(name ?? '')
    .normalize('NFC')
    .replace(/[<>:"/\\|?* -]/g, ' ')  // illegal on Windows
    .replace(/\s+/g, '_')                              // spaces to underscores
    .replace(/_{2,}/g, '_')                            // never a run of underscores
    .replace(/^[._]+|[._]+$/g, '')                     // no leading/trailing dot or underscore
    .slice(0, 80)                                      // keep the whole path well inside limits
    .replace(/[._]+$/g, '');                           // the slice may have exposed a trailer
  return cleaned || 'Untitled';
}

/** `DDMMYYYY_HHMM` in local time. */
export function reportStamp(at = new Date()) {
  const d = at instanceof Date && !Number.isNaN(at.getTime()) ? at : new Date();
  return `${pad(d.getDate())}${pad(d.getMonth() + 1)}${d.getFullYear()}`
    + `_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

/**
 * Full report filename.
 *
 * `caseToken` names the case a report documents, for the one report that follows a case selection:
 * the Energy Modelling report can be exported for the original design or for any measure, and two
 * such exports in the same minute would otherwise collide on one filename and overwrite each
 * other. Omitted by every other report, which then keeps its historical name exactly.
 *
 * @param {{ projectName:string, kind:'daylight'|'energy'|'cfd', at?:Date, caseToken?:string }} args
 */
export function reportFileName({ projectName, kind, at = new Date(), caseToken = null }) {
  const k = REPORT_KINDS[kind];
  if (!k) throw new Error(`Unknown report kind "${kind}". Expected one of: ${Object.keys(REPORT_KINDS).join(', ')}.`);
  const kase = caseToken ? `_${sanitizeCaseToken(caseToken)}` : '';
  return `BIGProject_${sanitizeProjectNameForFile(projectName)}_${k.token}${kase}_${reportStamp(at)}.pdf`;
}

/**
 * Make a case name safe as a filename component, keeping the hyphen.
 *
 * A measure token is written "ECM2-Double_glazing": the hyphen separates the measure's number
 * from its name and is added deliberately, so unlike a project name it must survive. Everything
 * else goes through the same rules.
 */
export function sanitizeCaseToken(token) {
  const parts = String(token ?? '').split('-');
  const cleaned = parts.map((x) => sanitizeProjectNameForFile(x)).filter((x) => x && x !== 'Untitled');
  return cleaned.join('-').slice(0, 60).replace(/[._-]+$/g, '') || 'Case';
}
